import { useState } from "react";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STATUS_STYLES = {
  offPeak: "bg-green-300",
  peak: "bg-pink-600",
  publicHoliday: "bg-amber-400",
  closed: "bg-neutral-300",
};

const LEGEND = [
  { status: "offPeak", label: "Off Peak" },
  { status: "peak", label: "Peak" },
  { status: "publicHoliday", label: "Public Holiday" },
  { status: "closed", label: "Closure Dates" },
];

function readStored(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function dateKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function parseDate(value) {
  if (typeof value !== "string") return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );

  return dateKey(date) === value ? date : null;
}

function getStatus(date, dateStatuses) {
  const customStatus = dateStatuses[dateKey(date)];

  if (
    customStatus &&
    Object.prototype.hasOwnProperty.call(STATUS_STYLES, customStatus)
  ) {
    return customStatus;
  }

  return date.getDay() === 0 || date.getDay() === 6
    ? "peak"
    : "offPeak";
}

function buildCalendar(month) {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstWeekday = (new Date(year, monthIndex, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  // Полные недели, начиная с понедельника.
  const cellCount = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;

  return Array.from({ length: cellCount }, (_, index) => {
    const day = index - firstWeekday + 1;

    return day < 1 || day > daysInMonth
      ? null
      : new Date(year, monthIndex, day);
  });
}

export default function DatePage({
  dateStatuses = {},
  nextPath = "/time",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [today] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });

  const [attraction] = useState(() => {
    const selected =
      location.state?.selectedAttraction ||
      readStored("selectedAttraction");

    const requestedId = searchParams.get("attraction");

    return selected && (!requestedId || selected.id === requestedId)
      ? selected
      : null;
  });

  const [savedBooking] = useState(() => {
    const saved = readStored("visitBooking");

    return saved?.attraction?.id === attraction?.id
      ? saved
      : null;
  });

  const [residentType, setResidentType] = useState(
    savedBooking?.residentType === "mykad" ? "mykad" : "non-mykad"
  );

const [selectedDate, setSelectedDate] = useState(() => {
  const savedDate = parseDate(savedBooking?.visitDate);

  return savedDate && savedDate >= today
    ? dateKey(savedDate)
    : "";
});

  const [visibleMonth, setVisibleMonth] = useState(() => {
    const initialDate = parseDate(savedBooking?.visitDate);
    const date = initialDate && initialDate >= today ? initialDate : today;

    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  const [transitioning, setTransitioning] = useState(false);
  const [error, setError] = useState("");

  const cells = buildCalendar(visibleMonth);
  const selectedDateObject = parseDate(selectedDate);

  const canContinue = Boolean(
    attraction &&
    selectedDateObject &&
    selectedDateObject >= today &&
    getStatus(selectedDateObject, dateStatuses) !== "closed"
  );

  const canGoPrevious =
    visibleMonth.getFullYear() > today.getFullYear() ||
    (visibleMonth.getFullYear() === today.getFullYear() &&
      visibleMonth.getMonth() > today.getMonth());

  function changeMonth(offset) {
    setVisibleMonth((current) => {
      const next = new Date(
        current.getFullYear(),
        current.getMonth() + offset,
        1
      );

      const minimum = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );

      return next < minimum ? minimum : next;
    });
  }

  function handleMonthSelect(event) {
    setVisibleMonth(
      new Date(
        visibleMonth.getFullYear(),
        Number(event.target.value),
        1
      )
    );
  }

  function goToToday() {
    setVisibleMonth(
      new Date(today.getFullYear(), today.getMonth(), 1)
    );
  }

  function handleNext() {
    if (!canContinue || transitioning) return;

    const booking = {
      attraction,
      residentType,
      visitDate: selectedDate,
      dateStatus: getStatus(selectedDateObject, dateStatuses),
    };

    try {
      localStorage.setItem("visitBooking", JSON.stringify(booking));
    } catch {
      setError("Unable to save your selection. Please try again.");
      return;
    }

    setError("");
    setTransitioning(true);

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    window.setTimeout(
      () => {
        navigate(nextPath, { state: { booking } });
        window.scrollTo({ top: 0, behavior: "instant" });
      },
      reducedMotion ? 0 : 300
    );
  }

  return (
    <main
      aria-busy={transitioning}
      className={`bg-white px-4 pb-12 pt-12 font-sans text-black transition-opacity duration-300 motion-reduce:transition-none lg:pt-14 ${
        transitioning ? "opacity-0" : "opacity-100"
      }`}
    >
      <section
        aria-labelledby="resident-heading"
        className="mx-auto max-w-screen-xl text-center"
      >
        <h1
          id="resident-heading"
          className="text-3xl font-extrabold leading-tight lg:text-5xl"
        >
          Are you a local resident?
        </h1>

        <p className="mt-6 text-sm leading-relaxed lg:text-xl">
          Note: Visitor may be asked to present identification card upon request
        </p>

        <div
          role="group"
          aria-label="Resident type"
          className="mt-10 flex justify-center gap-6 lg:gap-20"
        >
          {[
            { value: "mykad", label: "MyKad" },
            { value: "non-mykad", label: "Non-MyKad" },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={residentType === option.value}
              onClick={() => setResidentType(option.value)}
              className={`flex h-36 w-36 items-center justify-center rounded-3xl text-base font-bold shadow-xl transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-4 lg:h-48 lg:w-52 lg:text-lg ${
                residentType === option.value
                  ? "bg-teal-500 text-white"
                  : "bg-white text-black hover:bg-neutral-50"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <h2 className="mt-9 text-3xl font-extrabold leading-tight lg:text-5xl">
          When do you intend to visit?
        </h2>
      </section>

      <section
        aria-label="Select visit date"
        className="mx-auto mt-8 max-w-3xl bg-white p-3 shadow-lg ring-1 ring-black/5 lg:p-4"
      >
        <div className="flex items-center justify-between gap-3 px-2">
          <div className="flex items-center gap-2 text-lg text-neutral-800 lg:text-xl">
            <label htmlFor="visit-month" className="sr-only">
              Month
            </label>

            <select
              id="visit-month"
              value={visibleMonth.getMonth()}
              onChange={handleMonthSelect}
              className="rounded-none border border-neutral-500 bg-white px-2 py-0.5 focus-visible:outline-teal-500"
            >
              {MONTHS.map((month, index) => (
                <option
                  key={month}
                  value={index}
                  disabled={
                    visibleMonth.getFullYear() === today.getFullYear() &&
                    index < today.getMonth()
                  }
                >
                  {month}
                </option>
              ))}
            </select>

            <span aria-live="polite">
              {visibleMonth.getFullYear()}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={goToToday}
              className="h-10 rounded-md bg-teal-500 px-3 text-sm font-bold text-white hover:bg-teal-600"
            >
              Today
            </button>

            <button
              type="button"
              aria-label="Previous month"
              disabled={!canGoPrevious}
              onClick={() => changeMonth(-1)}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-teal-500 text-white hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>

            <button
              type="button"
              aria-label="Next month"
              onClick={() => changeMonth(1)}
              className="flex h-10 w-10 items-center justify-center rounded-md bg-teal-500 text-white hover:bg-teal-600"
            >
              <FiChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-7">
          {WEEKDAYS.map((day) => (
            <div
              key={day}
              className="pb-3 text-center text-xs font-semibold text-neutral-500 lg:text-sm"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {cells.map((date, index) => {
            if (!date) {
              return (
                <div
                  key={`empty-${index}`}
                  aria-hidden="true"
                  className="h-20"
                />
              );
            }

            const key = dateKey(date);
            const status = getStatus(date, dateStatuses);
            const isPast = date < today;
            const isClosed = status === "closed";
            const isSelected = selectedDate === key;
            const isToday = key === dateKey(today);

            return (
           <button
  key={key}
  type="button"
  disabled={isPast || isClosed}
  aria-pressed={isSelected}
  aria-current={isToday ? "date" : undefined}
  aria-label={`${date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  })}, ${LEGEND.find((item) => item.status === status)?.label}`}
  onClick={() => {
    setSelectedDate(key);
    setError("");
  }}
  className={`flex h-20 min-w-0 flex-col items-center justify-between rounded-md px-1 py-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-600 lg:px-2 ${
    isSelected
      ? "bg-teal-500 text-white"
      : isPast || isClosed
        ? "cursor-not-allowed text-neutral-300"
        : isToday
          ? "text-teal-500 enabled:hover:bg-neutral-50"
          : "text-neutral-700 enabled:hover:bg-neutral-50"
  }`}
>
  <span>{date.getDate()}</span>

  <span
    aria-hidden="true"
    className={`h-2.5 w-full rounded-full ${
      isSelected
        ? "invisible"
        : isPast
          ? "bg-neutral-200"
          : STATUS_STYLES[status]
    }`}
  />
</button>
            );
          })}
        </div>

        <ul className="mt-5 space-y-1 px-1 pb-1 text-sm lg:text-lg">
          {LEGEND.map((item) => (
            <li key={item.status} className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                  STATUS_STYLES[item.status]
                }`}
              />
              {item.label}
            </li>
          ))}
        </ul>
      </section>

      {!attraction && (
        <p
          role="alert"
          className="mx-auto mt-6 max-w-3xl text-center text-sm text-red-600"
        >
          Please go back and select an attraction.
        </p>
      )}

      {error && (
        <p
          role="alert"
          className="mx-auto mt-6 max-w-3xl text-center text-sm text-red-600"
        >
          {error}
        </p>
      )}

      <div className="mx-auto mt-16 grid max-w-2xl grid-cols-2 gap-6 px-4 lg:gap-9">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="rounded-md bg-amber-300 py-4 text-sm font-bold text-white transition-colors hover:bg-amber-400"
        >
          Back
        </button>

        <button
          type="button"
          disabled={!canContinue || transitioning}
          onClick={handleNext}
          className="rounded-md bg-teal-500 py-4 text-sm font-bold text-white transition-colors hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Next Step
        </button>
      </div>
    </main>
  );
}